import { c as _c } from "react/compiler-runtime";
import { Box, Typography } from '@mui/material';
import { Fragment } from 'react';
import type * as v from 'valibot';
import type { StructuredContentDocumentWithRenderer } from '../../hydration/types';
import { renderStructuredContent } from '../../renderContext';
import { getAllChildrenStrings, markNestedTypes } from '../../utils';
import { getHeadingId } from '../CreditCardsTableOfContents/utils';
import { H2Wrapper } from './H2Wrapper';
import type { enhancedContentHeadingSCSchema } from './schema';
type Variant = 'title' | 'textLargeBold' | 'textLarge' | 'textBold' | 'text' | 'textSmall';
const headingStyleMap: Record<number, Variant> = {
  1: 'title',
  2: 'textLargeBold',
  3: 'textLarge',
  4: 'textBold',
  5: 'text',
  6: 'textSmall'
};
const wrapperMap: Record<number, React.ElementType> = {
  2: H2Wrapper,
  3: H3Wrapper
};
export type EnhancedContentHeadingProps = StructuredContentDocumentWithRenderer<v.InferInput<typeof enhancedContentHeadingSCSchema>>;
export function EnhancedContentHeading(sccProps) {
  const $ = _c(16);
  const {
    props
  } = sccProps;
  const component = `h${props.level}`;
  const variant = headingStyleMap[props.level];
  let WrapperComponent;
  if (props.renderBasic) {
    WrapperComponent = DefaultWrapper;
  } else {
    WrapperComponent = wrapperMap[props.level] ?? DefaultWrapper;
  }
  let id;
  let t0;
  if ($[0] !== sccProps) {
    const childrenString = getAllChildrenStrings(sccProps);
    t0 = getHeadingId(childrenString);
    $[0] = sccProps;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const slugifiedId = t0;
  if (props.level === 2) {
    id = slugifiedId;
  }
  const T0 = WrapperComponent;
  const t1 = `content-heading-${slugifiedId}`;
  let t2;
  if ($[2] !== id) {
    t2 = id && {
      id
    };
    $[2] = id;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      lineHeight: "inherit !important",
      fontWeight: 700
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== props.children) {
    t4 = [props.children].flat().map(_temp);
    $[5] = props.children;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== component || $[8] !== t1 || $[9] !== t2 || $[10] !== t4 || $[11] !== variant) {
    t5 = <Typography component={component} variant={variant} data-testid={t1} {...t2} sx={t3}>{t4}</Typography>;
    $[7] = component;
    $[8] = t1;
    $[9] = t2;
    $[10] = t4;
    $[11] = variant;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== WrapperComponent || $[14] !== t5) {
    t6 = <T0>{t5}</T0>;
    $[13] = WrapperComponent;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
function _temp(child, idx) {
  if (typeof child === "string") {
    return <Box key={`content-heading-${idx}`} component="span" sx={{
      whiteSpace: "pre-wrap"
    }}>{child}</Box>;
  }
  return <Fragment key={`content-heading-doc-${idx}`}>{renderStructuredContent(markNestedTypes(child))}</Fragment>;
}
function H3Wrapper(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <Box paddingY={2} marginBottom={3} borderBottom="4px solid" borderColor="greens.main">{children}</Box>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function DefaultWrapper(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <>{children}</>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}